import { Eye, GripVertical, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useRealEstateSystem } from '../../hooks/useRealEstateSystem';
import { RealEstateForm } from './RealEstateForm';

interface RealEstateCardProps {
  property: any;
  onDelete: () => void;
  onDragStart: () => void;
  onEdit: () => void;
}

function RealEstateCard({ property, onDelete, onDragStart, onEdit }: RealEstateCardProps) {
  const { colors } = useDesignColors();

  const formatPrice = (price: number, currency: string) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: currency
    }).format(price);
  };

  return (
    <div 
      draggable
      onDragStart={onDragStart}
      className="group relative bg-white/5 backdrop-blur-xl rounded-xl border border-white/10 overflow-hidden hover:border-indigo-500/50 transition-all"
      style={{
        background: `linear-gradient(135deg, ${colors.gradientStart}10, ${colors.gradientEnd}10)`,
      }}
    >
      <div className="flex gap-4 p-4">
        {property.images?.[0] && (
          <div className="relative w-32 h-32 rounded-lg overflow-hidden bg-black/20 group-hover:scale-105 transition-transform">
            <img
              src={property.images[0]}
              alt={property.title}
              className="w-full h-full object-cover"
            />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <h4 className="text-lg font-medium text-white truncate">{property.title}</h4>
          </div>
          
          {property.description && (
            <p className="text-sm text-gray-400 mt-1 line-clamp-2">{property.description}</p>
          )}
          
          <div className="flex flex-wrap gap-2 mt-3">
            {property.type && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm bg-white/5 text-gray-300">
                {property.type}
              </span>
            )}
            {property.transaction_type && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm bg-white/5 text-gray-300">
                {property.transaction_type === 'sale' ? 'Vente' : 'Location'}
              </span>
            )}
            {property.price && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm bg-white/5 text-gray-300">
                {formatPrice(property.price, property.currency)}
              </span>
            )}
          </div>

          {/* Rentabilité et revente estimée */}
          {(property.target_roi !== undefined || property.estimated_resale !== undefined) && (
            <div className="flex flex-wrap gap-4 mt-3 text-sm">
              {property.target_roi !== undefined && (
                <div className="flex items-center gap-1 text-gray-300">
                  <span className="text-indigo-400">Target profitability:</span>
                  <span>{property.target_roi}%</span>
                </div>
              )}
              
              {property.estimated_resale !== undefined && (
                <div className="flex items-center gap-1 text-gray-300">
                  <span className="text-indigo-400">Estimated resale:</span>
                  <span>{formatPrice(property.estimated_resale, property.currency)}</span>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 text-gray-400 hover:text-white transition-colors"
          >
            <Eye className="w-5 h-5" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
          >
            <Trash2 className="w-5 h-5" />
          </button>
          <div className="p-2 text-gray-400 cursor-move">
            <GripVertical className="w-5 h-5" />
          </div>
        </div>
      </div>
    </div>
  );
}

export function RealEstateList() {
  const { chatbotPublicId } = useChatbot();
  const { properties, deleteProperty, reorderProperties, loading } = useRealEstateSystem();
  const { accentColor } = useDesignColors();
  const [editingProperty, setEditingProperty] = useState<any | null>(null);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedItem, setDraggedItem] = useState<any | null>(null);

  const handleDragStart = (index: number) => {
    setDraggedItem(index);
  };

  const handleDragOver = async (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedItem === null || draggedItem === index || !chatbotPublicId) return;

    const newProperties = [...properties];
    const draggedProperty = newProperties[draggedItem];
    newProperties.splice(draggedItem, 1);
    newProperties.splice(index, 0, draggedProperty);

    await reorderProperties(chatbotPublicId, newProperties.map(p => p.id));
    setDraggedItem(index);
  };

  const handleDragEnd = async () => {
    if (!isDragging || draggedItem === null) return;

    // Reset the drag state
    setIsDragging(false);
    setDraggedItem(null);
  };

  const handleDelete = async (propertyId: string) => {
    if (chatbotPublicId) {
      await deleteProperty(chatbotPublicId, propertyId);
    }
  };

  if (loading.properties) {
    return (
      <div className="flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between">
        <h3 className="text-lg font-semibold text-white">Properties</h3>
        <button
          onClick={() => setIsAddingNew(true)}
          disabled={loading.properties}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          Add
        </button>
      </div>

      {isAddingNew && (
        <RealEstateForm onClose={() => setIsAddingNew(false)} />
      )}

      {editingProperty && (
        <RealEstateForm 
          property={editingProperty} 
          onClose={() => setEditingProperty(null)} 
        />
      )}

      {properties.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-400">No properties</p>
        </div>
      ) : (
        <div className="space-y-4">
          {properties.map((property, index) => (
            <div
              key={property.id}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={() => handleDragEnd()}
            >
              <RealEstateCard
                property={property}
                onDelete={() => handleDelete(property.id)}
                onDragStart={() => handleDragStart(index)}
                onEdit={() => setEditingProperty(property)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}